import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import OAuthSignIn from './pages/OAuthSignIn';
import EuGrantLearnMore from './pages/EUGrantLearnMore';
import Chat from './pages/Chat';
import ChatComponent from './pages/TextStreamingChat';
import { RequireLogin } from '../helper';
import { UserProvider } from '../hooks/useUser';
import { commonRoutes } from '../commonPages/CommonRoutes';
import UserSettings from './pages/UserSettings';
import { Toaster } from 'react-hot-toast';
import Playground from './pages/Playground';
import ApiDocs from './pages/ApiDocs';
import ManageDatabase from './pages/ManageDatabase';
import ManageApi from './pages/ManageApi';
import ExternalOAuthSignIn from './pages/ExternalOAuthSignIn';
import NLLanding from './pages/NLLanding';
import AboutUs from './pages/AboutUs';
import Research from './pages/Research';
import Analysis from './pages/Analysis';
import MissionOS from './pages/MissionOS';
import StartupPage from './pages/StartupPage';
import VraagMijnOverheidProductPage from './website/pages/VraagMijnOverheidProductPage';
import Applications from './website/pages/Applications';
import Platform from './website/pages/Platform';
import Models from './website/pages/Models';
import Asclepius510k from './website/pages/Asclepius510k';
import Contact from './website/pages/Contact';
import News from './website/pages/News';
import Trust from './website/pages/Trust';
import Brand from './website/pages/Brand';
import PressKit from './website/pages/PressKit';
import ResearchLimit from './website/pages/ResearchLimit';
import ResearchWorldview from './website/pages/ResearchWorldview';
import ResearchVectorOntologies from './website/pages/ResearchVectorOntologies';
import PrivacyPolicy from './website/pages/PrivacyPolicy';
import Terms from './website/pages/Terms';

const ThoughtfulOasisIndex = () => {
    return(
        <UserProvider mountingScreen={<></>}>
            <Helmet>
            <meta name="description" content="Thoughtful Oasis is an AI research lab building infrastructure for trustworthy applications in mission-critical industries." />
            <meta name="keywords" content="search, engine, language, industries, retrieval, augmented, generation, ontology, vector, database" />
            <title>Thoughtful Oasis</title>
            <link rel="icon" href="/favicon.svg" />
        </Helmet>
            <BrowserRouter future={{ v7_startTransition: true }} basename={''}>
                <Toaster />
                <Routes>
                    {/* Visitor / website routes */}
                    <Route path="/" element={<NLLanding />} />
                    <Route path="/about" element={<AboutUs />} />
                    <Route path="/about-us" element={<Navigate to="/about" replace />} />
                    <Route path="/research" element={<Research />} />
                    <Route path="/research/limit" element={<ResearchLimit />} />
                    <Route path="/research/worldview" element={<ResearchWorldview />} />
                    <Route path="/research/vector-ontologies" element={<ResearchVectorOntologies />} />
                    <Route path="/applications" element={<Applications />} />
                    <Route path="/platform" element={<Platform />} />
                    <Route path="/models" element={<Models />} />
                    <Route path="/models/asclepius-510k" element={<Asclepius510k />} />
                    <Route path="/mission-os" element={<MissionOS />} />
                    <Route path="/vmo" element={<VraagMijnOverheidProductPage />} />
                    <Route path="/woo-requests" element={<Navigate to="/vmo" replace />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/news" element={<News />} />
                    <Route path="/trust" element={<Trust />} />
                    <Route path="/documents" element={<Brand />} />
                    <Route path="/documents/press-kit" element={<PressKit />} />
                    {/* Legacy routes — /brand was the old URL, kept as redirects. */}
                    <Route path="/brand" element={<Navigate to="/documents" replace />} />
                    <Route path="/brand/press-kit" element={<Navigate to="/documents/press-kit" replace />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/tos" element={<Terms />} />
                    <Route path="/startup" element={<StartupPage />} />

                    {/* App routes — unchanged */}
                    <Route path="/playground" element={<Playground />} />
                    <Route path="/knowledge-base/:Database_id" element={<ManageDatabase />} />
                    <Route path="/knowledge-base/:Database_id/analysis" element={<Analysis />} />
                    <Route path="/manage-api" element={<ManageApi />} />
                    <Route path="/api-docs" element={<ApiDocs />} />
                    <Route path="/chat" element={<Chat />} />
                    <Route path="/streaming-chat" element={<ChatComponent />} />
                    <Route path="eu-grant-learn-more" element={<RequireLogin><EuGrantLearnMore /></RequireLogin>} />
                    <Route path="settings" element={<RequireLogin><UserSettings /></RequireLogin>} />
                    <Route path="oauth-signup" element={<OAuthSignIn />} />
                    <Route path="external-oauth-signup" element={<ExternalOAuthSignIn />} />

                    {/* Legacy .html redirects so external links keep working */}
                    <Route path="/index.html" element={<Navigate to="/" replace />} />
                    <Route path="/about.html" element={<Navigate to="/about" replace />} />
                    <Route path="/research.html" element={<Navigate to="/research" replace />} />
                    <Route path="/research-limit.html" element={<Navigate to="/research/limit" replace />} />
                    <Route path="/research-worldview.html" element={<Navigate to="/research/worldview" replace />} />
                    <Route path="/research-vector-ontologies.html" element={<Navigate to="/research/vector-ontologies" replace />} />
                    <Route path="/applications.html" element={<Navigate to="/applications" replace />} />
                    <Route path="/platform.html" element={<Navigate to="/platform" replace />} />
                    <Route path="/models.html" element={<Navigate to="/models" replace />} />
                    <Route path="/predicate-equivalence.html" element={<Navigate to="/models/asclepius-510k" replace />} />
                    <Route path="/mission-os.html" element={<Navigate to="/mission-os" replace />} />
                    <Route path="/vmo.html" element={<Navigate to="/vmo" replace />} />
                    <Route path="/contact.html" element={<Navigate to="/contact" replace />} />
                    <Route path="/news.html" element={<Navigate to="/news" replace />} />
                    <Route path="/trust.html" element={<Navigate to="/trust" replace />} />
                    <Route path="/brand.html" element={<Navigate to="/documents" replace />} />
                    <Route path="/brand/press-kit.html" element={<Navigate to="/documents/press-kit" replace />} />
                    <Route path="/documents.html" element={<Navigate to="/documents" replace />} />
                    <Route path="/documents/press-kit.html" element={<Navigate to="/documents/press-kit" replace />} />
                    <Route path="/privacy-policy-en.html" element={<Navigate to="/privacy-policy" replace />} />
                    <Route path="/privacy-policy-nl.html" element={<Navigate to="/privacy-policy" replace />} />
                    <Route path="/terms-of-service-en.html" element={<Navigate to="/tos" replace />} />
                    <Route path="/terms-of-service-nl.html" element={<Navigate to="/tos" replace />} />

                    {/* Skip /privacy-policy and /tos from commonRoutes — overridden above with website pages */}
                    {commonRoutes.filter((r: any) => {
                        const p = r?.props?.path;
                        return p !== 'privacy-policy' && p !== 'tos';
                    })}
                    <Route path="*" element={<Navigate to="/" />} />
                </Routes>
            </BrowserRouter>
        </UserProvider>
    )
}

export default ThoughtfulOasisIndex;
